import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import AnchoredDropdown from 'features/agent-analytics/components/AnchoredDropdown';
import { StatusTag } from 'features/issues/components/IssueTags';

/**
 * The issues a run or span is evidence for, from GET /api/failures/signals —
 * the data the retired Signals tab showed. `null` while loading.
 */
export function useLinkedIssues(teamId, kind, id) {
  const [issues, setIssues] = useState(null);
  useEffect(() => {
    if (!teamId || !id) { setIssues([]); return undefined; }
    let live = true;
    setIssues(null);
    axiosInstance.get('/failures/signals', { params: { team_id: teamId, kind, id } })
      .then(({ data }) => { if (live) setIssues(data?.issues || []); })
      .catch(() => { if (live) setIssues([]); });
    return () => { live = false; };
  }, [teamId, kind, id]);
  return issues;
}

/**
 * The meta-grid cell beside Capabilities: one issue as a link, several behind
 * a dropdown, as Capabilities does. A row opens the issue on the Issues page.
 * Returns the cell `{ label, wide, content }`, or null when there is nothing
 * linked (an empty "Issues: none" cell on every healthy run is noise).
 */
export function linkedIssuesCell(issues, openIssue) {
  if (!issues || issues.length === 0) return null;
  const linkCls = 'text-left text-[13px] text-[color:var(--feedback-error)] underline-offset-2 hover:underline';
  return {
    label: issues.length > 1 ? `Issues (${issues.length})` : 'Issue',
    wide: true,
    content: issues.length > 1 ? (
      <AnchoredDropdown
        trigger={
          <button type="button" className={`inline-flex max-w-full items-center gap-0.5 ${linkCls}`}>
            <span className="truncate">{issues[0].title}</span>
            <ChevronDown size={12} className="mt-px flex-none" />
          </button>
        }
      >
        {issues.map((i) => (
          <button
            key={i.id}
            type="button"
            onClick={() => openIssue(i.id)}
            className="flex w-full max-w-[360px] items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-[color:var(--surface-sunken)]"
          >
            <span className="min-w-0 flex-1 truncate font-medium text-[color:var(--text-primary)]" title={i.title}>{i.title}</span>
            <StatusTag status={i.status} />
          </button>
        ))}
      </AnchoredDropdown>
    ) : (
      <button type="button" onClick={() => openIssue(issues[0].id)} className={`max-w-full truncate ${linkCls}`} title={issues[0].title}>
        {issues[0].title}
      </button>
    ),
  };
}

/** Opens an issue in the Issues page's drawer. */
export function useOpenIssue() {
  const navigate = useNavigate();
  const { team } = useParams();
  const teamSlug = team || localStorage.getItem('selectedTeam') || '';
  return (issueId) => navigate(`/${encodeURIComponent(teamSlug)}/agent-analytics/issues?issue=${encodeURIComponent(issueId)}`);
}
